import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  UnitWorkflowConfigs,
  WorkflowConfig,
  useWorkflows,
} from '~/data/workflows';
import { getInitialWorkflowConfig } from '~/data/workflows/data';
import { WorkflowProcessType } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/data';

type PropsType = {
  unitId: number;
};

export type UseWorkflowConfigurationsDataOutput = {
  unitId: number;
  unitWorkflowConfigs: UnitWorkflowConfigs;
  deleteWorkflow: (workflowId: string) => Promise<unknown>;
};

export const useWorkflowConfigsData = (
  props: PropsType,
): UseWorkflowConfigurationsDataOutput => {
  const {
    getUnitWorkflowConfigs,
    deleteUnitWorkflowConfig,
    workflowConfigs,
    unitWorkflowConfigs,
  } = useWorkflows();

  const { unitId: activeUnitId } = props;

  const populateMissingProcessTypeWorkflowConfigs = useCallback(
    (workflowConfigs: WorkflowConfig[]): WorkflowConfig[] => {
      return Object.values(WorkflowProcessType).map((processType) => {
        const foundConfig = workflowConfigs.find(
          (config) => config.processType === processType,
        );
        const emptyConfig: WorkflowConfig = {
          ...getInitialWorkflowConfig(),
          processType: processType.toString(),
          unitId: 0,
        };

        return !!foundConfig ? foundConfig : emptyConfig;
      });
    },
    [],
  );

  const noWorkflows: UnitWorkflowConfigs = useMemo(
    () => ({
      unitId: activeUnitId,
      workflows: populateMissingProcessTypeWorkflowConfigs([]),
    }),
    [activeUnitId, populateMissingProcessTypeWorkflowConfigs],
  );

  const [unitWorkflows, setUnitWorkflows] =
    useState<UnitWorkflowConfigs>(noWorkflows);

  const loadData = useCallback(() => {
    getUnitWorkflowConfigs(activeUnitId)
      .then((unitWorkflowConfigs) => {
        // Populate with undefined process types
        setUnitWorkflows({
          ...unitWorkflowConfigs,
          workflows: populateMissingProcessTypeWorkflowConfigs(
            unitWorkflowConfigs.workflows,
          ),
        });
      })
      .catch((err) => {
        // Reset to empty
        setUnitWorkflows(noWorkflows);
      });
  }, [
    activeUnitId,
    getUnitWorkflowConfigs,
    setUnitWorkflows,
    noWorkflows,
    populateMissingProcessTypeWorkflowConfigs,
  ]);

  // Refresh data when redux data (workflowConfigs, unitWorkflowConfigs) has changed
  useEffect(() => {
    loadData();
  }, [loadData, workflowConfigs, unitWorkflowConfigs]);

  return {
    unitId: activeUnitId,
    unitWorkflowConfigs: unitWorkflows,
    deleteWorkflow: deleteUnitWorkflowConfig,
  };
};
